import { useQuery } from '@apollo/client';
import InfoIcon from '@atlaskit/icon/glyph/info';
import { N200 } from '@atlaskit/theme/colors';
import Tooltip from '@atlaskit/tooltip';
import { generatedTypesNew, Maybe, PaymentMethod, PaymentMethodType, Refunds, SortDirection } from '@css-xp/graphtypes';
import {
  CreditCard,
  EntitlementDetails,
  FetchInvoicesForEntitlementQuery,
  FetchInvoicesForEntitlementQueryVariables,
  InvoicePaymentMethod,
} from '@css-xp/graphtypes/src/generated-types-new';
import { NEW_DATE_FORMAT, NEW_DATE_TIME_FORMAT, TRANSACTION_ACCOUNT_PATH_2 } from '@atlassian/cssxp-typescommon';
import { NO_DETAILS_TO_SHOW } from '@atlassian/cssxp-utils-entitlement';
import { GloballyAllowedSidebar } from '@atlassian/cssxp-utils-generic';
import { getIdentifier } from '@atlassian/cssxp-utils-invoice';
import {
  ColGroup,
  DismissableInlineMessage,
  FormattedDate,
  formattedV2,
  Icon,
  InvoiceUtil,
  NoDataContentInTable,
  OpenSidebarRow,
  TableBody,
  TableHead,
  useSidebar,
} from '@css-xp/base';
import { InvoiceCell } from '@css-xp/base/common-tabs';
import { CustomDynamicResultTable } from '@css-xp/base/custom-dynamic-result-table';
import { InvoiceChildRefundRow } from '@css-xp/billing-v2';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import memoize from 'lodash/memoize';
import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchInvoicesForEntitlementQuery } from '../../../../entitlement-page/src/queries/fetch-invoices-for-entitlement';
import './invoice-tab.scss';

export const DEFAULT_ROWS_PER_PAGE = 8;
export const DEFAULT_PAGE = 1;

interface Props {
  id: string;
  entitlement: EntitlementDetails;
  availablePaymentMethods?: Maybe<Maybe<PaymentMethod>[]> | undefined;
  isCloudSiteInvoiceTab?: boolean;
}

export const getIsFreeInvoice = memoize((paymentMethod?: InvoicePaymentMethod | null) => {
  return paymentMethod === InvoicePaymentMethod.NOPAYMENT;
});

const getDueOnCell = (cellName: string) => {
  return (
    <th>
      <Tooltip content={'Overdues in open invoices will be marked in red'} tag="span">
        <InfoIcon label="Info" size="small" primaryColor={N200} />
      </Tooltip>
      <span className="due-on-text">{cellName}</span>
    </th>
  );
};

const getBillingAccountCell = (cellName: string) => {
  return (
    <th>
      <Tooltip content={'Also known as transaction account'} tag="span">
        <InfoIcon label="Info" size="small" primaryColor={N200} />
      </Tooltip>
      <span className="due-on-text">{cellName}</span>
    </th>
  );
};

const getColumns = () => {
  const columns = [
    {
      name: 'Invoice number',
    },
    {
      name: 'Billing account',
      renderComponent: getBillingAccountCell('Billing account'),
    },
    {
      name: 'Due',
      renderComponent: getDueOnCell('Due'),
    },
    {
      name: 'Issued on',
    },
    {
      name: 'Payment',
    },
    {
      name: 'Amount',
    },
  ];

  return columns;
};

export const defaultSearchOptions: FetchInvoicesForEntitlementQueryVariables['searchOptions'] = {
  first: 8,
  sort: {
    sortFields: ['createdDate', 'id'],
    sortFieldsDirections: [SortDirection.DESC, SortDirection.DESC],
  },
};

export const InvoiceTab = ({ id, entitlement, availablePaymentMethods, isCloudSiteInvoiceTab = false }: Props) => {
  const [rowsPerPage, setRowsPerPage] = useState<number>(DEFAULT_ROWS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState<number>(DEFAULT_PAGE);
  const { isSidebarOpen, payload } = useSidebar();
  const { refetch, loading, error, data } = useQuery<
    FetchInvoicesForEntitlementQuery,
    FetchInvoicesForEntitlementQueryVariables
  >(fetchInvoicesForEntitlementQuery, {
    variables: {
      id: id,
      searchOptions: defaultSearchOptions,
    },
    displayName: 'fetchInvoicesForEntitlementQuery',
    notifyOnNetworkStatusChange: true,
  });
  // variables to use for paging and populating our template
  const totalCount = data?.entitlementDetails.invoices.totalCount ?? 0;
  const hasNoResults = totalCount === 0;
  const nodes = data?.entitlementDetails?.invoices?.nodes;
  const txaId = entitlement?.transactionAccountId;

  const onPageChange = useCallback(
    (newPage: number) => {
      setCurrentPage(newPage);
      refetch({
        searchOptions: {
          ...defaultSearchOptions,
          first: rowsPerPage,
          offset: newPage !== 1 ? newPage * rowsPerPage - rowsPerPage : 0,
        },
        // filterOptions,
      });
    },
    [rowsPerPage, refetch]
  );

  const handleRowChange = useCallback((rowsCount: number) => {
    setRowsPerPage(rowsCount);
    setCurrentPage(1);
  }, []);

  const getPaymentMethod = (paymentId: string | null, invoice: generatedTypesNew.Invoice) => {
    if (paymentId) {
      const selectedPaymentMethod = availablePaymentMethods?.find((paymentMethod) => paymentMethod?.id === paymentId);
      if (selectedPaymentMethod) {
        if (selectedPaymentMethod?.type === PaymentMethodType.CARD) {
          const creditCard = selectedPaymentMethod as CreditCard;
          return <span>{`**** **** **** ${creditCard?.card?.last4Numbers ?? ''}`}</span>;
        } else if (selectedPaymentMethod?.type === PaymentMethodType.DEFERRED) {
          return <span>Payment on terms</span>;
        }
      } else {
        if (
          invoice?.paymentMethod === InvoicePaymentMethod.PAIDCC ||
          invoice?.paymentMethod === InvoicePaymentMethod.STRIPE_CARD
        ) {
          return <span>{`**** **** **** ${invoice?.creditCard?.last4Numbers ?? ''}`}</span>;
        } else if (invoice && invoice.paymentMethod === InvoicePaymentMethod.DEFERRED) {
          return <span>Payment on terms</span>;
        }
      }
    }
    return <NoDataContentInTable />;
  };

  const nextInvoiceTime: any = (entitlement as EntitlementDetails)?.nextInvoiceTimeAt;
  let nextInvoice = <>Invoice will be generated soon.</>;
  if (nextInvoiceTime) {
    nextInvoice = (
      <>
        See
        <Link to={`${TRANSACTION_ACCOUNT_PATH_2}/${txaId}?inner_tab=billing-profiles`}>
          {' '}
          Account details {'>'} Billing profiles
        </Link>{' '}
        for more details.
      </>
    );
  }

  const renderActions = useCallback(() => {
    if (entitlement?.hasPendingInvoices) {
      return [
        () => (
          <div>
            <DismissableInlineMessage
              position="bottom-start"
              renderMessage={() => {
                return (
                  <>
                    <div className="generating-invoice-dialog-content">
                      {nextInvoiceTime ? (
                        <>
                          Invoices will be ready by <FormattedDate date={nextInvoiceTime} showTime />
                        </>
                      ) : (
                        <>We’re getting some invoices ready for this account</>
                      )}
                      . <div> {nextInvoice} </div>
                    </div>
                  </>
                );
              }}
              renderTitle={(toggleMessage) => (
                <div className="hoverable-icon tooltip-container" onClick={toggleMessage} data-testid="inline-message">
                  <Icon type="info" size="medium" /> <span className="tooltip-text">Generating invoices</span>
                </div>
              )}
            />
          </div>
        ),
      ];
    }
    return [];
  }, [entitlement, nextInvoiceTime]);

  const props = {
    children: (
      <>
        <ColGroup columns={getColumns()} />
        <TableHead columns={getColumns()} />
        <TableBody>
          {/*@ts-ignore*/}
          {(nodes ?? []).map((invoice: generatedTypesNew.Invoice, index: number) => {
            const {
              id,
              invoiceNumber,
              status,
              dueDate,
              grandTotal,
              paymentCurrency,
              createdDate,
              paymentMethod,
              sourceSystem,
              refunds,
              pdfLink,
              transactionAccount,
              paymentMethodId,
            } = invoice;

            const identifier = getIdentifier({
              id,
              invoiceNumber,
              sourceSystem,
            });
            const rowPayload = {
              invoiceId: identifier,
            };

            return (
              <>
                <OpenSidebarRow
                  key={`invoice-table-row-${index}`}
                  sidebar={GloballyAllowedSidebar.INVOICE_PREVIEW_V2}
                  payload={rowPayload}
                  sidebarOptions={{
                    isOverlay: true,
                    sidebarWidth: '70%',
                  }}
                  isSelected={false}
                  data-testid={`invoice-table-row-${index}`}
                >
                  <>
                    <td>
                      <InvoiceCell
                        status={status}
                        invoiceId={id}
                        invoiceNumber={invoiceNumber || ''}
                        paymentError={!!invoice?.dunningStatus?.length}
                        pdfLink={pdfLink}
                        transactionAccountId={transactionAccount?.[0]?.id}
                        grandTotal={grandTotal}
                        refunds={refunds as Refunds[]}
                        sourceSystem={sourceSystem || ''}
                        paymentMethod={paymentMethod as InvoicePaymentMethod}
                      />
                    </td>
                    <td>
                      {transactionAccount?.[0]?.transactionAccountNumber ? (
                        <Link
                          to={`${TRANSACTION_ACCOUNT_PATH_2}/${encodeURIComponent(
                            transactionAccount?.[0]?.transactionAccountNumber || ''
                          )}`}
                          onClick={(e) => e.stopPropagation()}
                          className="avatar-container"
                        >
                          <span>{transactionAccount?.[0]?.transactionAccountNumber}</span>
                        </Link>
                      ) : (
                        <NoDataContentInTable />
                      )}
                    </td>
                    <td>
                      {dueDate ? (
                        <div
                          className={classNames({
                            'overdue-date': InvoiceUtil.isDueDateOverdue(
                              status ?? '',
                              !!invoice?.dunningStatus?.length,
                              dueDate
                            ),
                          })}
                        >
                          <FormattedDate date={dueDate} format={NEW_DATE_FORMAT} />
                        </div>
                      ) : (
                        <NoDataContentInTable />
                      )}
                    </td>
                    <td>
                      {createdDate ? (
                        <FormattedDate date={createdDate} showTime={true} format={NEW_DATE_TIME_FORMAT} />
                      ) : (
                        <NoDataContentInTable />
                      )}
                    </td>
                    <td>{getPaymentMethod(paymentMethodId as string, invoice)}</td>
                    <td className="invoice-amount-cell">
                      {!getIsFreeInvoice(paymentMethod) && grandTotal ? (
                        <formattedV2.FormattedCurrencyV2
                          value={grandTotal}
                          currency={paymentCurrency}
                          showCurrencyCode
                        />
                      ) : (
                        'Free'
                      )}
                    </td>
                  </>
                </OpenSidebarRow>
                <>
                  {!isEmpty(refunds) &&
                    //@ts-ignore
                    (refunds ?? []).map((ccpCreditNote: Refunds) => {
                      const refundPayload = {
                        refundId: ccpCreditNote?.creditNoteNumber || ccpCreditNote?.id,
                      };
                      const isChildRowSelected = isSidebarOpen && isEqual(refundPayload, payload);
                      return (
                        <InvoiceChildRefundRow
                          key={ccpCreditNote.id}
                          parentColumns={6}
                          hideActions={false}
                          refund={ccpCreditNote}
                          isSelected={isChildRowSelected}
                          isCloudSiteInvoiceTab={isCloudSiteInvoiceTab}
                        />
                      );
                    })}
                </>
              </>
            );
          })}
        </TableBody>
      </>
    ),
    noResults: hasNoResults,
    isLoading: loading,
    error: error?.toString(),
    heading: 'Invoices',
    selectableResults: true,
    isPaginationEnabled: totalCount > DEFAULT_ROWS_PER_PAGE,
    totalCount: totalCount,
    rowsPerPage: rowsPerPage,
    currentPage: currentPage,
    onPageChange: onPageChange,
    onRowChange: handleRowChange,
    testId: 'transaction-account-invoice-tab',
    emptyStateProps: {
      header: '',
      description: NO_DETAILS_TO_SHOW,
    },
    actions: renderActions(),
  };

  return (
    <div data-testid="statuspage-invoice-tab" className="invoice-table-container">
      <CustomDynamicResultTable {...props}></CustomDynamicResultTable>
    </div>
  );
};
